import isValueFilled from "@utils/isValueFilled";
import { t, type TFunction } from "i18next";
import { z } from "zod";

export const VISA_ON_ARRIVAL_CODE = "VA";

export type PortSecuritySchemaOptions = {
  visaExemptNationalities?: ReadonlySet<string>;
};

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z0-9 ]*$/, {
      message: t("form-validation.invalid-character", { label }),
    });
const requiredWithSpecialChars = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const requiredWithHyphen = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z0-9 -]*$/, {
      message: t("form-validation.invalid-character", { label }),
    });

const optional = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z0-9 ]*$/, {
      message: t("form-validation.invalid-character", { label }),
    })
    .optional();

const optionalWithSpecialChars = (max: number, label = t("form-validation.field")) =>
  z.string().max(max, t("form-validation.max-length", { label, max })).optional();

const optionalWithLanguageChars = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z0-9 \-.,;]*$/, {
      message: t("form-validation.invalid-character", { label }),
    })
    .optional();

const emailSchema = (max: number, label = t("security-clearance.email")) =>
  z
    .string()
    .max(max, t("form-validation.max-length", { label, max }))
    .optional()
    .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
      message: t("form-validation.email", { label }),
    });

// Required email - same format as Requesting Sponsor Email
const requiredEmailSchema = (
  translate: TFunction,
  max: number,
  label = translate("security-clearance.email"),
  invalidEmailMessage?: string,
) =>
  z
    .string()
    .min(1, translate("form-validation.required", { label }))
    .max(max, translate("form-validation.max-length", { label, max }))
    .superRefine((val, ctx) => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: invalidEmailMessage ?? translate("form-validation.email", { label }),
        });
        return;
      }

      if (/^\d+$/.test(val.split("@")[0] ?? "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate("form-validation.email-numbers-only"),
        });
      }
    });

const optionalOfficePhoneSchema = (
  translate: TFunction,
  max: number,
  label = translate("security-clearance.telephone"),
) =>
  z
    .string()
    .max(max, translate("form-validation.office-contact-max-length", { label, max }))
    .optional();

const requiredTelephoneSchema = (max: number, label = t("security-clearance.telephone")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .regex(/^\+[1-9]\d{6,14}$/, {
      message: t("form-validation.telephone", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }));

const optionalStrictTelephoneSchema = (max: number, label = t("security-clearance.telephone")) =>
  z
    .string()
    .max(max, t("form-validation.max-length", { label, max }))
    .refine((val) => !val || /^\+[1-9]\d{6,14}$/.test(val), {
      message: t("form-validation.telephone", { label }),
    })
    .optional();

const salarySchema = (max: number, label = t("security-clearance.salary")) =>
  z
    .string()
    .regex(/^[a-zA-Z0-9 ,._@#$%*&-]*$/, {
      message: t("form-validation.regex", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }))
    .optional();

const uidSchema = (max: number, label = t("security-clearance.uid-number")) =>
  z
    .string()
    .regex(/^[a-zA-Z0-9 ,._@#$%*&-]*$/, {
      message: t("form-validation.regex", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }))
    .optional();

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.required", { label }),
    }),
    z.string().min(1, t("form-validation.required", { label })),
  ]);

const optionalDate = () => z.union([z.date().nullable().optional(), z.string()]);

const addDuplicatePrimitiveIssues = ({
  values,
  path,
  message,
  ctx,
}: {
  values?: string[];
  path: string;
  message: string;
  ctx: z.RefinementCtx;
}) => {
  if (!Array.isArray(values) || values.length === 0) {
    return;
  }

  const firstIndexByValue = new Map<string, number>();

  values.forEach((value, index) => {
    const normalizedValue = String(value ?? "")
      .trim()
      .toLowerCase();
    if (!normalizedValue) {
      return;
    }

    const firstIndex = firstIndexByValue.get(normalizedValue);
    if (firstIndex === undefined) {
      firstIndexByValue.set(normalizedValue, index);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [path, firstIndex],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [path, index],
    });
  });
};

export const passportNumberSchema = (
  max: number,
  label = t("security-clearance.passport-number"),
) =>
  z
    .string()
    .superRefine((val, ctx) => {
      const min = 6;
      if (!val) return; // Only validate if value is present

      // Not alphanumeric
      if (!/^[a-zA-Z0-9]+$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.regex-no-special-chars", { label }),
        });
        return;
      }

      // Too short
      if (val.length < min) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.min-characters", { min }),
        });
        return;
      }

      // Too long (> max, expected 15)
      if (val.length > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length", { label, max }),
        });
        return;
      }

      // Pattern validation:
      // Accept 6-10 alphanumerics OR exactly 15 digits
      const isValid = /^[a-zA-Z0-9]{6,10}$/.test(val) || /^\d{15}$/.test(val);

      if (!isValid) {
        // Reuse character error key for invalid pattern outside defined lengths (11-14 alphanumerics or 15 mixed)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.invalid-passport"),
        });
      }
    })
    .optional();

export const requiredPassportNumberSchema = (
  max: number,
  label = t("security-clearance.passport-number"),
) =>
  passportNumberSchema(max, label).superRefine((val, ctx) => {
    if (!val) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", { label }),
      });
    }
  });

const toDate = (v: unknown): Date | null => {
  const d = v instanceof Date ? v : typeof v === "string" ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};
const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const today = () => startOfDay(new Date());

const refineRequiredDate =
  (label: string, pass: (d: Date) => boolean, messageKey: string) => (schema: z.ZodTypeAny) =>
    schema.refine(
      (val) => {
        const d = toDate(val);
        if (!d) return true;
        return pass(startOfDay(d));
      },
      { message: t(messageKey, { label }) },
    );

const refineOptionalDate =
  (label: string, pass: (d: Date) => boolean, messageKey: string) => (schema: z.ZodTypeAny) =>
    schema.refine(
      (val) => {
        if (val == null || val === "") return true;
        const d = toDate(val);
        if (!d) return false;
        return pass(startOfDay(d));
      },
      { message: t(messageKey, { label }) },
    );

export const emiratesIdNumberSchema = (label = t("security-clearance.emirates-id-no")) =>
  z
    .string()
    .superRefine((val, ctx) => {
      if (!val) return; // Only validate if value is present
      if (val.length < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 18,
          type: "string",
          inclusive: true,
          message: t("form-validation.min-numbers", { label, min: 15 }),
        });
        return;
      }
      if (val.length > 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: 18,
          type: "string",
          inclusive: true,
          message: t("form-validation.max-length", { label, max: 15 }),
        });
        return;
      }
      if (!/^\d{3}-\d{4}-\d{7}-\d$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.invalid-emirates-id", { label }),
        });
      }
    })
    .optional();

const ltToday = (d: Date) => d < today();
const gtToday = (d: Date) => d > today();
const leToday = (d: Date) => d <= today();
const geToday = (d: Date) => d >= today();

const requiredPastDate = (label: string) =>
  refineRequiredDate(label, ltToday, "form-validation.must-be-in-past")(requiredDate(label));

const optionalPastDate = (label: string) =>
  refineOptionalDate(label, ltToday, "form-validation.must-be-in-past")(optionalDate());
const optionalFutureDate = (label: string) =>
  refineOptionalDate(label, gtToday, "form-validation.must-be-in-future")(optionalDate());
const optionalNotPastDate = (label: string) =>
  refineOptionalDate(label, geToday, "form-validation.must-not-be-in-past")(optionalDate());
const optionalNotFutureDate = (label: string) =>
  refineOptionalDate(label, leToday, "form-validation.must-not-be-in-future")(optionalDate());
const requiredFutureDate = (label: string) =>
  refineRequiredDate(label, gtToday, "form-validation.must-be-in-future")(requiredDate(label));

// not currently used, but maybe usefull in the future:
// const optionalNotPastDate = (label: string) => refineOptionalDate(label, geToday, "form-validation.must-not-be-in-past")(optionalDate());
type states = {
  state: string;
};

export const uaeStates: states[] = [
  { state: "Abu Dhabi" },
  { state: "Dubai" },
  { state: "Sharjah" },
  { state: "Ajman" },
  { state: "Umm Al Quwain" },
  { state: "Ras Al Khaimah" },
  { state: "Fujairah" },
];

export const getPortSecurityFormSchema = (
  t: TFunction,
  { visaExemptNationalities = new Set<string>() }: PortSecuritySchemaOptions = {},
) =>
  z
    .object({
      // Bio Data
      fullNameEn: requiredWithHyphen(200, t("security-clearance.full-name-en")),
      fullNameAr: requiredWithSpecialChars(200, t("security-clearance.full-name-ar")),
      surname: required(200, t("security-clearance.surname")),
      alias: optional(200, t("security-clearance.alias")),
      placeOfBirth: required(200, t("security-clearance.place-of-birth")),
      dateOfBirth: requiredPastDate(t("security-clearance.date-of-birth")),
      presentNationality: requiredWithSpecialChars(
        200,
        t("security-clearance.present-nationality"),
      ),
      presentNationalityArabic: requiredWithSpecialChars(
        200,
        t("security-clearance.present-nationality-arabic"),
      ),
      pastNationality: optionalWithSpecialChars(200, t("security-clearance.past-nationality")),
      emailAddress: requiredEmailSchema(
        t,
        50,
        t("security-clearance.individual-email-address"),
        t("security-clearance.email-well-formed"),
      ),
      mobileNumber: requiredTelephoneSchema(200, t("security-clearance.individual-mobile-number")),
      sect: optional(200, t("security-clearance.sect")),
      religion: optional(200, t("security-clearance.religion")),
      placeOfEntry: optional(200, t("security-clearance.place-of-entry")),
      dateOfEntry: optionalDate(),
      sponsorUponEntry: optional(200, t("security-clearance.sponsor-upon-entry")),
      // Current Sponsor Data
      currentSponsor: required(200, t("security-clearance.current-sponsor")),
      occupation: required(200, t("security-clearance.occupation")),
      presentWorkplace: required(200, t("security-clearance.present-workplace")),
      occupationAr: requiredWithSpecialChars(200, t("security-clearance.occupation-ar")),
      officeFax: optionalOfficePhoneSchema(t, 20, t("security-clearance.office-fax")),
      bank: optional(200, t("security-clearance.bank")),
      officeTelephone: optionalOfficePhoneSchema(t, 20, t("security-clearance.office-telephone")),
      salary: salarySchema(200, t("security-clearance.salary")),

      // Particulars of Passport
      passportPlaceOfIssue: required(200, t("security-clearance.passport-place-of-issue")),
      passportNumber: requiredPassportNumberSchema(10, t("security-clearance.passport-number")),
      passportExpiryDate: requiredFutureDate(t("security-clearance.passport-expiry-date")),
      passportDateOfIssue: requiredPastDate(t("security-clearance.passport-date-of-issue")),

      // Particulars of Emirates Id
      emiratesIdPlaceOfIssue: optional(200, t("security-clearance.emirates-id-place-of-issue")),
      emiratesIdNumber: emiratesIdNumberSchema(t("security-clearance.emirates-id-no")),

      emiratesIdExpiryDate: optionalFutureDate(t("security-clearance.emirates-id-expiry-date")),
      emiratesIdDateOfIssue: optionalNotFutureDate(
        t("security-clearance.emirates-id-date-of-issue"),
      ),

      // Residence / VISA
      visaPlaceOfIssue: optional(200, t("security-clearance.place-of-issue")),
      residenceVisaNo: optional(200, t("security-clearance.residence-visa-no")),
      visaType: optional(200, t("security-clearance.visa-type")),
      visaDateOfIssue: optionalNotFutureDate(t("security-clearance.visa-date-of-issue")),
      visaExpiryDate: optionalNotPastDate(t("security-clearance.visa-expiry-date")),

      // Academic Qualifications
      academicQualification: optional(200, t("security-clearance.academic-qualification")),
      placeOfStudy: optional(200, t("security-clearance.place-of-study")),
      university: optional(200, t("security-clearance.university")),
      languagesKnown: optionalWithLanguageChars(200, t("security-clearance.languages-known")),
      certificateDate: optionalPastDate(t("security-clearance.certificate-date")),

      // Social Status
      spouseName: optional(200, t("security-clearance.spouse-name")),
      spouseNationality: optionalWithSpecialChars(200, t("security-clearance.nationality")),
      spouseBirthPlace: optional(200, t("security-clearance.place-of-birth")),
      spouseBirthDate: optionalPastDate(t("security-clearance.date-of-birth")),
      spouseOccupation: optional(200, t("security-clearance.occupation")),
      spouseWorkPlace: optional(200, t("security-clearance.work-place")),

      // Children
      children: z
        .array(
          z
            .string()
            .max(
              200,
              t("form-validation.max-length", {
                label: t("security-clearance.child-name"),
                max: 200,
              }),
            )
            .regex(/^[A-Za-z0-9 ]*$/, {
              message: t("form-validation.invalid-character", {
                label: t("security-clearance.child-name"),
              }),
            })
            .optional(),
        )
        .optional(),

      // Father
      fatherName: required(200, t("security-clearance.father-name")),
      fatherNationality: requiredWithSpecialChars(200, t("security-clearance.father-nationality")),
      fatherBirthPlace: required(200, t("security-clearance.father-birth-place")),
      fatherBirthDate: optionalPastDate(t("security-clearance.date-of-birth")),
      fatherWorkPlace: optional(200, t("security-clearance.father-work-place")),
      fatherOccupation: optional(200, t("security-clearance.father-occupation")),

      // Mother
      motherName: required(200, t("security-clearance.mother-name")),
      motherNationality: requiredWithSpecialChars(200, t("security-clearance.mother-nationality")),
      motherBirthPlace: required(200, t("security-clearance.mother-birth-place")),
      motherBirthDate: optionalPastDate(t("security-clearance.date-of-birth")),
      motherWorkPlace: optional(200, t("security-clearance.mother-work-place")),
      motherOccupation: optional(200, t("security-clearance.mother-occupation")),

      // Other Blood Relations
      bloodRelations: z
        .array(
          z.object({
            relation: optional(200, t("security-clearance.relation")),
            name: optional(200, t("security-clearance.name")),
            nationality: optionalWithSpecialChars(200, t("security-clearance.nationality")),
            workPlace: optional(200, t("security-clearance.work-place")),
          }),
        )
        .optional(),

      // Friends
      friends: z
        .array(
          z.object({
            name: optional(200, t("security-clearance.name")),
            nationality: optionalWithSpecialChars(200, t("security-clearance.nationality")),
            workPlace: optional(200, t("security-clearance.work-place")),
          }),
        )
        .optional(),

      // Residence
      zone: optional(200, t("security-clearance.zone")),
      ownerName: optional(200, t("security-clearance.owner-name")),
      phoneNo: optionalStrictTelephoneSchema(200, t("security-clearance.phone-number")),
      street: optional(200, t("security-clearance.street")),
      mobileNo: optionalStrictTelephoneSchema(200, t("security-clearance.mobile-number")),
      buildingNo: optional(200, t("security-clearance.building-number")),
      pagerNo: optional(200, t("security-clearance.pager-number")),
      flatNo: optional(200, t("security-clearance.flat-number")),
      poBoxNo: optional(200, t("security-clearance.po-box-number")),
      floorNo: optional(200, t("security-clearance.floor-number")),

      // Previous occupations
      prevOccupations: z
        .array(
          z
            .string()
            .max(
              200,
              t("form-validation.max-length", {
                label: t("security-clearance.previous-occupation"),
                max: 200,
              }),
            )
            .regex(/^[A-Za-z0-9 ]*$/, {
              message: t("form-validation.invalid-character", {
                label: t("security-clearance.previous-occupation"),
              }),
            }),
        )
        .optional(),

      // Countries Visited
      countriesVisited: z
        .array(
          z
            .string()
            .max(
              200,
              t("form-validation.max-length", { label: t("security-clearance.country"), max: 200 }),
            ),
        )
        .optional(),

      // Countries Worked In
      countriesWorkedIn: z
        .array(
          z
            .string()
            .max(
              200,
              t("form-validation.max-length", { label: t("security-clearance.country"), max: 200 }),
            ),
        )
        .optional(),

      // Particulars of Vehicle
      typeOfVehicle: z.string().optional(),
      licenseNumber: optional(200, t("security-clearance.license-number")),
      placeOfIssue: z.string().optional(),
      licensedBy: optional(200, t("security-clearance.licensed-by")),
      dateOfIssue: optionalNotFutureDate(t("security-clearance.date-of-issue")),
      plateNumber: optional(200, t("security-clearance.plate-number")),
      dateOfLicenseExpiry: optionalFutureDate(t("security-clearance.date-of-license-expiry")),
      plateCode: optional(200, t("security-clearance.plate-code")),

      // Military Field
      hasServed: z.boolean(),
      countryName: optional(200, t("security-clearance.country-name")),
      serviceType: optional(200, t("security-clearance.service-type")),
      rank: optional(200, t("security-clearance.rank")),
      durationOfService: optional(200, t("security-clearance.duration")),
      militaryServiceStartDate: optionalPastDate(
        t("security-clearance.military-service-start-date-2"),
      ),
      militaryServiceEndDate: optionalDate(),

      // Signature
      signatureAgreement: z.boolean().refine((val) => val, {
        message: t("form-validation.signature-agreement"),
      }),
      signatureName: z.string().optional(),
      signatureDate: z.string().optional(),

      // Visit Details and Documents
      photograph: z.any().refine((val) => Array.isArray(val) && val.length > 0, {
        message: t("form-validation.required", { label: t("security-clearance.photograph") }),
      }),
      passportFront: z.any().optional(),
      passportBack: z.any().optional(),
      emiratesIdFront: z.any().optional(),
      emiratesIdBack: z.any().optional(),
      visa: z.any().optional(),

      // FOTT Entry
      FOTTEntry: z.boolean(),

      // Visit Details
      sponsorCompanyEnglish: optional(200, t("security-clearance.sponsor-company")),
      sponsorCompanyArabic: optional(200, t("security-clearance.sponsor-company")),
      requestingSponsorEmailAddress: emailSchema(
        200,
        t("security-clearance.requesting-sponsor-email-address"),
      ),
      visitorCompanyEnglish: required(200, t("security-clearance.visitor-company")),
      visitorCompanyArabic: requiredWithSpecialChars(200, t("security-clearance.visitor-company")),
      entryPurposeEnglish: required(1000, t("security-clearance.entry-purpose-en")),
      entryPurposeArabic: requiredWithSpecialChars(1000, t("security-clearance.entry-purpose-ar")),
      securityClearanceValidity: required(200, t("security-clearance.security-clearance-validity")),
      uidNumber: uidSchema(200),
      gdpr: z.boolean().refine((val) => val, {
        message: t("form-validation.gdpr-agreement"),
      }),
    })
    .superRefine((obj, ctx) => {
      addDuplicatePrimitiveIssues({
        values: obj.prevOccupations,
        path: "prevOccupations",
        message: t("security-clearance.previous-occupation-duplicate"),
        ctx,
      });
      addDuplicatePrimitiveIssues({
        values: obj.children?.map((child) => child || ""),
        path: "children",
        message: t("security-clearance.child-duplicate"),
        ctx,
      });
      addDuplicatePrimitiveIssues({
        values: obj.countriesVisited,
        path: "countriesVisited",
        message: t("security-clearance.country-duplicate"),
        ctx,
      });
      addDuplicatePrimitiveIssues({
        values: obj.countriesWorkedIn,
        path: "countriesWorkedIn",
        message: t("security-clearance.country-duplicate"),
        ctx,
      });

      const isNationalityUAE = obj.presentNationality === "United Arab Emirates";
      const isVisaExemptNationality = visaExemptNationalities.has(obj.presentNationality);
      const isVisaOnArrival = obj.visaType === VISA_ON_ARRIVAL_CODE;
      if (!Array.isArray(obj.passportFront) || obj.passportFront.length === 0) {
        ctx.addIssue({
          path: ["passportFront"],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t("security-clearance.passport"),
          }),
        });
      }
      if (!Array.isArray(obj.passportBack) || obj.passportBack.length === 0) {
        ctx.addIssue({
          path: ["passportBack"],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t("security-clearance.passport"),
          }),
        });
      }
      if (isNationalityUAE) {
        if (!obj.emiratesIdPlaceOfIssue) {
          ctx.addIssue({
            path: ["emiratesIdPlaceOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id-place-of-issue"),
            }),
          });
        }
        if (!obj.emiratesIdNumber) {
          ctx.addIssue({
            path: ["emiratesIdNumber"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id-no"),
            }),
          });
        }
        if (!obj.emiratesIdDateOfIssue) {
          ctx.addIssue({
            path: ["emiratesIdDateOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id-date-of-issue"),
            }),
          });
        }
        if (
          !obj.emiratesIdFront ||
          !Array.isArray(obj.emiratesIdFront) ||
          obj.emiratesIdFront.length === 0
        ) {
          ctx.addIssue({
            path: ["emiratesIdFront"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id"),
            }),
          });
        }
        if (
          !obj.emiratesIdBack ||
          !Array.isArray(obj.emiratesIdBack) ||
          obj.emiratesIdBack.length === 0
        ) {
          ctx.addIssue({
            path: ["emiratesIdBack"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id"),
            }),
          });
        }

        const hasVisaDetails =
          obj.visaPlaceOfIssue ||
          obj.residenceVisaNo ||
          obj.visaType ||
          obj.visaExpiryDate ||
          obj.visaDateOfIssue;

        if (hasVisaDetails && !isVisaExemptNationality) {
          if (!obj.visaType) {
            ctx.addIssue({
              path: ["visaType"],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.required", {
                label: t("security-clearance.visa-type"),
              }),
            });
          }
          if (!isVisaOnArrival) {
            if (!obj.visaPlaceOfIssue) {
              ctx.addIssue({
                path: ["visaPlaceOfIssue"],
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t("security-clearance.place-of-issue"),
                }),
              });
            }
            if (!obj.visaExpiryDate) {
              ctx.addIssue({
                path: ["visaExpiryDate"],
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t("security-clearance.visa-expiry-date"),
                }),
              });
            }
            if (!obj.visa || !Array.isArray(obj.visa) || obj.visa.length === 0) {
              ctx.addIssue({
                path: ["visa"],
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t("security-clearance.visa"),
                }),
              });
            }
          }
        }
      } else if (!isNationalityUAE && obj.presentNationality && !isVisaExemptNationality) {
        if (!obj.visaType) {
          ctx.addIssue({
            path: ["visaType"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.visa-type"),
            }),
          });
        }
        if (!isVisaOnArrival && !obj.visaPlaceOfIssue) {
          ctx.addIssue({
            path: ["visaPlaceOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.place-of-issue"),
            }),
          });
        }
        if (!isVisaOnArrival && !obj.visaExpiryDate) {
          ctx.addIssue({
            path: ["visaExpiryDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.visa-expiry-date"),
            }),
          });
        }
        if (
          !obj.passportFront ||
          !Array.isArray(obj.passportFront) ||
          obj.passportFront.length === 0
        ) {
          ctx.addIssue({
            path: ["passportFront"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport"),
            }),
          });
        }
        if (
          !obj.passportBack ||
          !Array.isArray(obj.passportBack) ||
          obj.passportBack.length === 0
        ) {
          ctx.addIssue({
            path: ["passportBack"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport"),
            }),
          });
        }
        if (!isVisaOnArrival && (!obj.visa || !Array.isArray(obj.visa) || obj.visa.length === 0)) {
          ctx.addIssue({
            path: ["visa"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.visa"),
            }),
          });
        }
        if (
          (!Array.isArray(obj.emiratesIdFront) || obj.emiratesIdFront.length === 0) &&
          obj.emiratesIdNumber
        ) {
          ctx.addIssue({
            path: ["emiratesIdFront"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id"),
            }),
          });
        }
        if (
          (!Array.isArray(obj.emiratesIdBack) || obj.emiratesIdBack.length === 0) &&
          obj.emiratesIdNumber
        ) {
          ctx.addIssue({
            path: ["emiratesIdBack"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id"),
            }),
          });
        }
        if (!obj.passportPlaceOfIssue) {
          ctx.addIssue({
            path: ["passportPlaceOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-place-of-issue"),
            }),
          });
        }
        if (!obj.passportNumber) {
          ctx.addIssue({
            path: ["passportNumber"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-number"),
            }),
          });
        }
        if (!obj.passportDateOfIssue) {
          ctx.addIssue({
            path: ["passportDateOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-date-of-issue"),
            }),
          });
        }
        if (!obj.passportExpiryDate) {
          ctx.addIssue({
            path: ["passportExpiryDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-expiry-date"),
            }),
          });
        }
      }
      if (obj.emiratesIdNumber) {
        if (!obj.emiratesIdPlaceOfIssue) {
          ctx.addIssue({
            path: ["emiratesIdPlaceOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id-place-of-issue"),
            }),
          });
        }
        if (!obj.emiratesIdDateOfIssue) {
          ctx.addIssue({
            path: ["emiratesIdDateOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.emirates-id-date-of-issue"),
            }),
          });
        }
      }
      if (obj.passportNumber) {
        if (!obj.passportPlaceOfIssue) {
          ctx.addIssue({
            path: ["passportPlaceOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-place-of-issue"),
            }),
          });
        }
        if (!obj.passportDateOfIssue) {
          ctx.addIssue({
            path: ["passportDateOfIssue"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-date-of-issue"),
            }),
          });
        }
        if (!obj.passportExpiryDate) {
          ctx.addIssue({
            path: ["passportExpiryDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("security-clearance.passport-expiry-date"),
            }),
          });
        }
      }
      if (obj.hasServed) {
        if (!obj.countryName) {
          ctx.addIssue({
            path: ["countryName"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.military-service-required", {
              label: t("security-clearance.name-of-country"),
            }),
          });
        }
        if (!obj.serviceType) {
          ctx.addIssue({
            path: ["serviceType"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.military-service-required", {
              label: t("security-clearance.service-type"),
            }),
          });
        }
        if (!obj.rank) {
          ctx.addIssue({
            path: ["rank"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.military-service-required", {
              label: t("security-clearance.rank"),
            }),
          });
        }
        if (!obj.durationOfService) {
          ctx.addIssue({
            path: ["durationOfService"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.military-service-required", {
              label: t("security-clearance.service-duration"),
            }),
          });
        }
        if (!obj.militaryServiceStartDate) {
          ctx.addIssue({
            path: ["militaryServiceStartDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.military-service-required", {
              label: t("security-clearance.military-service-start-date-2"),
            }),
          });
        }
      }

      // Social Status: if any field is filled, all Social Status fields are required
      const socialStatusFields: Array<{
        key:
          | "spouseName"
          | "spouseNationality"
          | "spouseBirthPlace"
          | "spouseBirthDate"
          | "spouseOccupation"
          | "spouseWorkPlace";
        label: string;
      }> = [
        { key: "spouseName", label: t("security-clearance.spouse-name") },
        { key: "spouseNationality", label: t("security-clearance.nationality") },
        { key: "spouseBirthPlace", label: t("security-clearance.place-of-birth") },
        { key: "spouseBirthDate", label: t("security-clearance.date-of-birth") },
        { key: "spouseOccupation", label: t("security-clearance.occupation") },
        { key: "spouseWorkPlace", label: t("security-clearance.work-place") },
      ];
      const anySocialStatusFilled = socialStatusFields.some(({ key }) => isValueFilled(obj[key]));
      if (anySocialStatusFilled) {
        for (const { key, label } of socialStatusFields) {
          if (!isValueFilled(obj[key])) {
            ctx.addIssue({
              path: [key],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.required", { label }),
            });
          }
        }
      }

      const get = (v: unknown) => (toDate(v) ? startOfDay(toDate(v)!) : null);

      // Passport: issue <= expiry (and both valid)
      {
        const issue = get(obj.passportDateOfIssue);
        const exp = get(obj.passportExpiryDate);
        if (issue && exp && !(issue < exp)) {
          ctx.addIssue({
            path: ["passportExpiryDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.expiry-after-issue", {
              label: t("security-clearance.passport"),
            }),
          });
        }
      }

      // Visa: issue <= expiry
      {
        if (!isVisaOnArrival) {
          const issue = get(obj.visaDateOfIssue);
          const exp = get(obj.visaExpiryDate);
          if (issue && exp && !(issue < exp)) {
            ctx.addIssue({
              path: ["visaExpiryDate"],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.expiry-after-issue", {
                label: t("security-clearance.visa"),
              }),
            });
          }
        }
      }

      // Driving license: dateOfIssue vs dateOfLicenseExpiry (both optional)
      {
        const issue = get(obj.dateOfIssue);
        const exp = get(obj.dateOfLicenseExpiry);
        if (issue && exp && !(issue < exp)) {
          ctx.addIssue({
            path: ["dateOfLicenseExpiry"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.expiry-after-issue", {
              label: t("security-clearance.license"),
            }),
          });
        }
      }

      // Military: start <= end (when both provided)
      {
        const start = get(obj.militaryServiceStartDate);
        const end = get(obj.militaryServiceEndDate);
        if (start && end && !(start <= end)) {
          ctx.addIssue({
            path: ["militaryServiceEndDate"],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.end-after-start", {
              label: t("security-clearance.military-service"),
            }),
          });
        }
      }
    });

export type PortSecurityFormData = z.infer<ReturnType<typeof getPortSecurityFormSchema>>;
